import './styles/pemlab-design-system.css';
import './styles/embed.css';
import { useState, useEffect } from 'react';
import { FormProvider, useForm } from '@/contexts/FormContext';
import {
  Introduction,
  Question,
  ProgressBar,
  ClientForm,
  Confirmation,
  DebugPanel,
} from '@/components';
import { APIService } from '@services/api';
import { IframeService } from '@services/iframe-service';
import { StorageService } from '@services/storage-service';
import { UrlEncoderService } from '@services/url-encoder-service';
import { ProductEngine } from '@services/product-engine';
import type { Answer, ClientInfo } from '@/types';

// Separate component that uses the form context
function FormContent() {
  const {
    questions,
    allQuestions,
    loading,
    error,
    refetch,
    currentStep,
    answers,
    clientInfo,
    totalPoints,
    saveAnswer,
    saveClientInfo,
    nextStep,
    previousStep,
    getProductRecommendations,
    getBonusProducts,
    currentQuestion,
  } = useForm();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedResults, setSavedResults] = useState<any>(null);
  const [showSavedResults, setShowSavedResults] = useState(false);
  const [decodedResults, setDecodedResults] = useState<any>(null);
  const [isReconstructing, setIsReconstructing] = useState(false);

  // Check for result parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resultId = urlParams.get('result');
    const encodedData = urlParams.get('r');
    
    if (encodedData) {
      // Decode from URL
      console.log('Encoded data found in URL');
      const decoded = UrlEncoderService.decodeResults(encodedData);
      if (decoded) {
        console.log('Decoded results:', decoded);
        setDecodedResults(decoded);
        setShowSavedResults(true);
        setIsReconstructing(true);
      }
    } else if (resultId) {
      // Legacy support for localStorage results
      console.log('Result ID found in URL:', resultId);
      const results = StorageService.getResults(resultId);
      console.log('Saved results from localStorage:', results);
      if (results) {
        setSavedResults(results);
        setShowSavedResults(true);
      } else {
        console.log('No results found for ID:', resultId);
      }
    }
  }, []);

  // Initialize iframe service and track progress
  useEffect(() => {
    IframeService.updateState(currentStep, questions.length + 2);
    IframeService.sendProgress(currentStep, questions.length + 2, totalPoints);
  }, [currentStep, questions.length, totalPoints]);

  // Handle redirect
  const handleRedirect = () => {
    const products = getProductRecommendations();
    const redirectUrl = APIService.getRedirectUrl(products);
    window.location.href = redirectUrl;
  };

  // Handle viewing saved results
  const handleViewSavedResults = () => {
    const results = StorageService.getResults();
    if (results) {
      setSavedResults(results);
      setShowSavedResults(true);
    }
  };

  // Reconstruct results from decoded URL data
  useEffect(() => {
    if (isReconstructing && decodedResults && allQuestions.length > 0) {
      console.log('Reconstructing results from decoded data...');
      
      // Create a product engine to recalculate products
      const engine = new ProductEngine();
      const reconstructedAnswers = new Map<string, Answer>();
      
      // Process each answer code - use allQuestions to include conditional questions
      allQuestions.forEach(question => {
        const answerValues: string[] = [];
        
        // Find matching options for this question by code
        question.options.forEach(option => {
          if (decodedResults.answers.includes(option.code)) {
            answerValues.push(option.label);
            
            // Add recommendations to engine
            if (option.products && option.products.length > 0) {
              engine.addRecommendation(question.code, option.products, option.points);
            }
            
            // Add exclusions
            if (option.exclusions && option.exclusions.length > 0) {
              engine.addExclusion(option.exclusions);
            }
            
            // Add alternatives
            if (option.alternatives) {
              Object.entries(option.alternatives).forEach(([original, alternative]) => {
                engine.addAlternative(original, alternative);
              });
            }
          }
        });
        
        if (answerValues.length > 0) {
          reconstructedAnswers.set(question.id, {
            questionId: question.id,
            values: answerValues,
            timestamp: new Date().toISOString(),
            points: 0
          });
        }
      });
      
      // Get reconstructed products
      const reconstructedProducts = engine.getRecommendations();
      const bonusProducts = engine.getProductCodes().filter(code => 
        reconstructedProducts.find(p => p.code === code && p.reason.includes('bonus-points'))
      );
      const regularProducts = reconstructedProducts.filter(p => !p.reason.includes('bonus-points'));
      
      // Create saved results object
      const reconstructed = {
        clientInfo: decodedResults.clientInfo as ClientInfo,
        recommendedProducts: regularProducts,
        bonusProducts: bonusProducts.map(code => ({
          code,
          name: code.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          count: 1,
          reason: ['bonus']
        })),
        answers: Object.fromEntries(reconstructedAnswers),
        questions: questions
      };
      
      setSavedResults(reconstructed);
      setIsReconstructing(false);
    }
  }, [isReconstructing, decodedResults, allQuestions]);

  // Show saved results if requested (check this BEFORE loading state)
  if (showSavedResults && savedResults) {
    // Show the saved results
    return (
      <div className="app-container">
        <Confirmation
          clientInfo={savedResults.clientInfo}
          products={savedResults.recommendedProducts}
          bonusProducts={savedResults.bonusProducts.map((p: any) => p.code)}
          totalPoints={0}
          redirectUrl=""
          answers={new Map(Object.entries(savedResults.answers || {}))}
          questions={savedResults.questions || questions}
          onRedirect={handleRedirect}
        />
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="app-container">
        <div className="container">
          <div className="flex-center" style={{ minHeight: '50vh' }}>
            <div className="text-center">
              <h2>⏳ Chargement du formulaire...</h2>
              <p className="text-large">Veuillez patienter</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="app-container">
        <div className="container">
          <div className="flex-center" style={{ minHeight: '50vh' }}>
            <div className="card" style={{ maxWidth: '600px' }}>
              <h2>❌ Erreur de chargement</h2>
              <p className="text-large mb-lg">{error}</p>
              <button className="btn btn-primary" onClick={refetch}>
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle answer from Question component
  const handleAnswer = (answer: Answer) => {
    saveAnswer(currentQuestion!.id, answer);
  };

  // Handle client form submission
  const handleClientSubmit = async (info: ClientInfo) => {
    console.log('🔵 Starting client form submission...');
    saveClientInfo(info);
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Get all products
      const allProducts = getProductRecommendations();
      const bonusProducts = getBonusProducts();
      console.log('📦 Products:', { allProducts, bonusProducts });

      // Generate result links
      const encodedData = UrlEncoderService.encodeResults(answers, info, questions);
      const baseUrl = window.location.origin;
      const resultLinks = {
        pageUrl: `${baseUrl}/?r=${encodedData}`,
        mailUrl: `${baseUrl}/mail?r=${encodedData}`
      };

      // Build and submit form data with links
      const formData = APIService.buildFormSubmission(
        info,
        allProducts,
        bonusProducts,
        totalPoints,
        answers,
        questions,
        resultLinks
      );
      console.log('📝 Form data built with links:', formData);

      // Check if we're in test mode to skip API call
      const urlParams = new URLSearchParams(window.location.search);
      const skipApi = urlParams.get('skipapi') === 'true';

      if (skipApi) {
        console.log('⚠️ Skipping API call (test mode)');
      } else {
        await APIService.submitForm(formData);
        console.log('✅ Form submitted successfully');
      }

      // Send complete event
      IframeService.sendComplete(allProducts, totalPoints);

      // Redirect to results page with encoded data
      console.log('➡️ Redirecting to results page...');
      window.location.href = resultLinks.pageUrl;
    } catch (error) {
      console.error('❌ Error submitting form:', error);
      const errorMessage = error instanceof Error ? error.message : 'Une erreur est survenue';
      setSubmitError(errorMessage);
      IframeService.sendError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render based on current step
  // Step 0: Introduction
  if (currentStep === 0) {
    return (
      <div className="app-container">
        <Introduction onStart={nextStep} onViewResults={handleViewSavedResults} />
      </div>
    );
  }

  // Steps 1 to N: Questions
  if (currentStep > 0 && currentStep <= questions.length) {
    const question = questions[currentStep - 1];
    const previousAnswer = answers.get(question.id);

    return (
      <div className="app-container">
        <div className="container mt-lg">
          <ProgressBar
            current={currentStep}
            total={questions.length + 1}
            animated
          />
          <Question
            question={question}
            previousAnswer={previousAnswer}
            onAnswer={handleAnswer}
            onNext={nextStep}
            onPrevious={previousStep}
            isFirst={currentStep === 1}
            isLast={currentStep === questions.length}
          />
        </div>
      </div>
    );
  }

  // Step N+1: Client form
  if (currentStep === questions.length + 1) {
    return (
      <div className="app-container">
        <div className="container mt-lg">
          <ProgressBar
            current={questions.length + 1}
            total={questions.length + 1}
          />
          <ClientForm
            onSubmit={handleClientSubmit}
            onPrevious={previousStep}
            initialData={clientInfo || undefined}
            isSubmitting={isSubmitting}
          />
          {submitError && (
            <div className="card mt-lg" style={{ maxWidth: '600px', margin: '0 auto' }}>
              <p className="text-center" style={{ color: 'var(--pemlab-pink)' }}>
                ❌ {submitError}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Step N+2: Confirmation
  console.log('🔍 Checking confirmation page condition:', {
    currentStep,
    expectedStep: questions.length + 2,
    hasClientInfo: !!clientInfo,
    shouldShowConfirmation: currentStep === questions.length + 2 && clientInfo,
  });

  if (currentStep === questions.length + 2 && clientInfo) {
    const products = getProductRecommendations();
    const bonusProducts = getBonusProducts();
    const nonBonusProducts = products.filter((p: string) => !bonusProducts.includes(p));

    return (
      <div className="app-container">
        <Confirmation
          clientInfo={clientInfo}
          products={nonBonusProducts.map((code: string) => ({
            code,
            name: code
              .split('-')
              .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' '),
            count: 1,
            reason: [],
          }))}
          bonusProducts={bonusProducts}
          totalPoints={totalPoints}
          redirectUrl={APIService.getRedirectUrl(products)}
          answers={answers}
          questions={questions}
          onRedirect={handleRedirect}
        />
      </div>
    );
  }

  // Fallback
  return null;
}

// Main App component with provider
function App() {
  const [showDebug, setShowDebug] = useState(false);

  // Initialize iframe service and check for debug mode
  useEffect(() => {
    // Check for test parameter
    const urlParams = new URLSearchParams(window.location.search);
    setShowDebug(urlParams.get('test') === 'test');

    IframeService.init();

    // Cleanup on unmount
    return () => {
      IframeService.destroy();
    };
  }, []);

  return (
    <FormProvider>
      <FormContent />
      {showDebug && <DebugPanel />}
    </FormProvider>
  );
}

export default App;
